import {
  Badge,
  Button,
  InputSelect,
  InputTextarea,
  InputWrapper,
  Select,
  type SelectItem,
} from '@vrplatform/components';
import {
  captureSentryError,
  type RequestBody,
  useApiMutation,
  useBilling,
  useInvalidateApiQueries,
  useQuery,
  useTrackingEvent,
  useTeam,
} from '@vrplatform/data';
import { Icon, Send2Icon, UsersIcon } from '@vrplatform/icons';
import type { ThemeColor } from '@vrplatform/theme';
import { showErrorNotification, showWarnNotification } from '@vrplatform/theme';
import { type Maybe, isUUID } from '@vrplatform/utils';
import {
  Box,
  Center,
  Group,
  LoadingOverlay,
  Modal,
  Stack,
  Text,
  Title,
  Tooltip,
  rem,
  useMantineTheme,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { formatUserName, orderByContact } from '@vrplatform/ui-common';
import { type JSX, useCallback, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { DrawerInfoCard } from '../../../drawers/_components';
import type { OwnerStatement } from '../statement/_types';
import {
  StatementDraftIcon,
  StatementInReviewIcon,
  StatementPublishedIcon,
} from './_icons';

type OwnerStatementStatus = OwnerStatement['status'];

type StatementProps = {
  statementId: Maybe<string>;
  periodId: Maybe<string>;
  month: string;
  status: OwnerStatementStatus;
  listingId: string;
  hasNoRows: boolean;
  currency: string;
  issues: OwnerStatement['issues'];
  isSource: boolean;
} | null;

type Props = {
  statement: StatementProps;
  type: 'button' | 'badge';
};

export function useLegderStatementStatusMutation(props?: {
  showError?: boolean;
  catchErrors?: boolean;
  invalidateQueries?: boolean;
}) {
  const track = useTrackingEvent();

  const {
    showError = true,
    catchErrors = true,
    invalidateQueries = true,
  } = props || {};

  const invalidate = useInvalidateApiQueries(['owner-statements']);

  const onError = (error: any) => {
    if (showError) {
      showErrorNotification({
        title: 'Failed to update status',
        message:
          error?.message ||
          "We couldn't update the status of the statement. Please try again and contact support if the issue persists.",
      });
    }
  };

  const onSettled = () => {
    invalidate();
  };

  const { mutateAsync: update, isPending: loadingUpdate } = useApiMutation(
    'put',
    '/statements/{id}',
    {
      onError,
      onSettled,
    }
  );

  const { mutateAsync: insert, isPending: loadingInsert } = useApiMutation(
    'post',
    '/statements',
    {
      onError,
      onSettled,
    }
  );

  const { mutateAsync: remove, isPending: loadingRemove } = useApiMutation(
    'delete',
    '/statements/{id}',
    {
      onError,
      onSettled,
    }
  );

  const updateStatus = useCallback(
    async (input: {
      statementId: Maybe<string>;
      month: string;
      status: Exclude<OwnerStatementStatus, 'posted' | 'void'>;
      emailDelivery: FormInputs | undefined;
      periodId: string;
      currency: string | undefined;
    }) => {
      const newStatus = input.status;

      try {
        const emailDelivery: RequestBody<'put:/statements/{id}'>['emailDelivery'] =
          newStatus !== 'published' ||
          !input.emailDelivery ||
          input.emailDelivery.notification === 'disabled'
            ? undefined
            : {
                immediate: input.emailDelivery.notification === 'now',
                message: input.emailDelivery.message,
              };

        if (input.statementId && isUUID(input.statementId)) {
          if (newStatus === 'draft') {
            await remove({
              params: {
                path: {
                  id: input.statementId,
                },
                query: {
                  onLocked: 'unlockAndDelete',
                },
              },
            });
          } else {
            await update({
              params: {
                path: {
                  id: input.statementId,
                },
              },
              body: {
                status: newStatus,
                emailDelivery,
              },
            });
          }
        } else {
          if (newStatus === 'draft') {
            if (catchErrors) {
              captureSentryError(
                'Tried to create draft statement without a statementId'
              );
            }
            if (showError) {
              showWarnNotification({
                title: 'Missing statement reference',
                message: 'Please reach out to support if the issue persists.',
              });
            }
            return;
          }

          await insert({
            body: {
              listingPeriodIds: [input.periodId],
              month: input.month.slice(0, 7),
              status: newStatus,
              emailDelivery,
              currency: input.currency,
            },
          });
        }

        track('owner_statement_status_changed', {
          ownerStatementId: input.statementId,
          status: newStatus,
        });
      } catch (error) {
        console.error(error);
        if (!catchErrors) throw error;
      } finally {
        if (invalidateQueries) invalidate();
      }
    },
    [
      update,
      insert,
      remove,
      invalidate,
      catchErrors,
      invalidateQueries,
      track,
      showError,
    ]
  );

  const loading = loadingInsert || loadingUpdate || loadingRemove;

  return useMemo(() => {
    return {
      loading,
      updateStatus,
    };
  }, [loading, updateStatus]);
}

const STATUS_COLORS: Record<OwnerStatementStatus, ThemeColor> = {
  draft: 'yellow',
  inReview: 'blue',
  published: 'green',
};

export const LedgerStatementStatusSelect = ({ type, statement }: Props) => {
  const { loading, updateStatus } = useLegderStatementStatusMutation();
  const [open, handlers] = useDisclosure(false);
  const { isReadOnly } = useBilling();

  const hasErrorIssue = useMemo(
    () => statement?.issues?.some((issue) => issue.severity === 'error'),
    [statement?.issues]
  );

  const { month, periodId, statementId, currency } = statement || {};

  const currentStatus = statement?.status || 'draft';

  const updateStatement = useCallback(
    (newStatus: OwnerStatementStatus) => {
      if (!month) return;

      if (hasErrorIssue && newStatus !== 'draft') {
        return showWarnNotification({
          title: 'Statement has issues',
          message: 'Only statements with issues can be reverted to draft.',
        });
      }

      if (!periodId)
        return showWarnNotification({
          title: 'Missing ownership',
          message: 'Please reach out to support if the issue persists.',
        });

      if (newStatus === currentStatus) return;

      const isModalConfirm = newStatus === 'published';

      if (isModalConfirm) {
        handlers.open();
      } else {
        updateStatus({
          month,
          periodId,
          statementId,
          status: newStatus,
          emailDelivery: undefined,
          currency,
        });
      }
    },
    [
      updateStatus,
      handlers.open,
      hasErrorIssue,
      month,
      periodId,
      statementId,
      currency,
      currentStatus,
    ]
  );

  const dropdownOptions = useStatementStatusSelectOptions({
    iconSize: 'sm',
  });
  const selectableOptions = useMemo(() => {
    if (!hasErrorIssue) return dropdownOptions;

    return dropdownOptions.map((option) => ({
      ...option,
      disabled: hasErrorIssue
        ? option.value !== 'draft'
        : option.value === 'draft',
    }));
  }, [dropdownOptions, hasErrorIssue]);
  const buttonOptions = useStatementStatusSelectOptions({
    iconSize: 'sm',
  });
  const badgeOptions = useStatementStatusSelectOptions({
    iconSize: 'xs',
  });

  const value = useMemo(() => {
    if (type === 'button')
      return buttonOptions.find((o) => o.value === currentStatus) || null;

    return badgeOptions.find((o) => o.value === currentStatus) || null;
  }, [currentStatus, buttonOptions, badgeOptions, type]);

  // if (statement?.hasNoRows || !statement?.periodId) {
  //   const label = statement?.hasNoRows
  //     ? 'Missing net revenue'
  //     : 'Missing ownership';

  //   if (type === 'button') {
  //     return (
  //       <Button disabled rightIcon={'ChevronIcon'}>
  //         {label}
  //       </Button>
  //     );
  //   }

  //   return (
  //     <MantineBagde
  //       variant="light"
  //       color={'gray'}
  //       component="button"
  //       disabled
  //       sx={(theme) => ({
  //         paddingInline: rem(5),
  //         textTransform: 'none',
  //         fontWeight: 500,
  //         color: theme.colors.gray[9],
  //         cursor: 'not-allowed',
  //       })}
  //     >
  //       {label}
  //     </MantineBagde>
  //   );
  // }

  if (statement?.isSource) {
    if (type === 'button') {
      return <Button disabled>External Source</Button>;
    }

    return (
      <Badge variant="light" color={'gray'}>
        External Source
      </Badge>
    );
  }

  const disabled =
    !statement?.periodId ||
    isReadOnly ||
    !statement ||
    statement.hasNoRows ||
    !statement.periodId;

  return (
    <>
      <StatusConfirmModal
        opened={open}
        closeModal={handlers.close}
        statement={statement}
      />
      <Select
        data={{
          options: selectableOptions,
          sort: null,
        }}
        dropdownProps={{
          position: 'bottom-end',
          width: 300,
          withinPortal: true,
        }}
        inputProps={{
          loadingMutation: loading,
          disabled,
        }}
        type="single"
        value={value}
        setValue={(_, update) => {
          if (update.type === 'removed') return;

          updateStatement(update.value.value as OwnerStatementStatus);
        }}
      >
        {({ loadingMutation, disabled, value, opened }) => {
          const item =
            (value as SelectItem<OwnerStatementStatus>) || badgeOptions.at(0);

          const status = item?.value;

          if (type === 'button') {
            return (
              <Button
                disabled={disabled}
                loading={loadingMutation}
                rightIcon="ChevronIcon"
                leftIcon={() => item.icon as JSX.Element} // this doesnt make any sense
                sx={(theme) => ({
                  '&, &:hover': {
                    backgroundColor: theme.colors[STATUS_COLORS[status]][0],
                  },
                })}
                loaderProps={{
                  color: STATUS_COLORS[status],
                }}
              >
                {item.label}
              </Button>
            );
          }
          const errorMessage = statement?.issues?.find(
            (x) => x.severity === 'error'
          )?.message;

          if (errorMessage || !statement?.periodId) {
            return (
              <Group>
                <LedgerStatementStatusBadge
                  disabled={disabled}
                  loading={!!loadingMutation}
                  status={status}
                  opened={opened}
                />
                {!statement?.periodId && (
                  <Tooltip label="Missing owner" multiline maw={400} withArrow>
                    <Icon
                      icon={'UserCrossIcon'}
                      size={16}
                      color={(theme) => theme.colors.neutral[6]}
                    />
                  </Tooltip>
                )}

                {errorMessage && !!statement?.periodId && (
                  <Tooltip label={errorMessage} multiline maw={400} withArrow>
                    <Icon
                      icon={'AlertCircleIcon'}
                      size={16}
                      color={(theme) => theme.colors.red[6]}
                    />
                  </Tooltip>
                )}
              </Group>
            );
          }

          return (
            <LedgerStatementStatusBadge
              disabled={disabled}
              loading={!!loadingMutation}
              status={status}
              opened={opened}
            />
          );
        }}
      </Select>
    </>
  );
};

export const LedgerStatementStatusBadge = ({
  status,
  disabled,
  loading,
  opened,
}: {
  status: OwnerStatementStatus;
  disabled: boolean | undefined;
  loading?: boolean;
  opened: boolean;
}) => {
  const theme = useMantineTheme();

  const options = useStatementStatusSelectOptions({
    iconSize: 'xs',
  });

  const value = options.find((x) => x.value === status);

  const item = (value as SelectItem<OwnerStatementStatus>) || options[0];

  return (
    <Badge
      component="button"
      disabled={disabled}
      appearance="tag"
      loading={loading}
      leftIcon={<Center>{item?.icon}</Center>}
      rightIcon={!disabled ? 'ChevronIcon' : undefined}
      sx={{
        cursor: disabled ? undefined : 'pointer',
        outline: opened ? `1px solid ${theme.colors.neutral[4]}` : undefined,
      }}
    >
      {item?.label}
    </Badge>
  );
};

type Notification = '2hrs' | 'disabled' | 'now';

type FormInputs = {
  notification: Notification;
  message: string;
};

export const useLedgerStatementUserAccessQuery = ({
  opened,
  statement,
}: {
  opened: boolean;
  statement: Maybe<{
    periodId: Maybe<string>;
  }>;
}) => {
  const [{ id: teamId }] = useTeam();

  return useQuery(
    (q, args) => {
      return q
        .listingOwnershipPeriods({
          where: {
            id: { _eq: args.periodId },
            tenantId: { _eq: args.teamId },
          },
          limit: 1,
        })
        .flatMap((period) =>
          period
            .members({
              order_by: [
                {
                  contact: orderByContact[1],
                },
              ],
              where: {
                contactId: { _is_null: false },
              },
            })
            .flatMap((periodMember) => {
              return (
                periodMember.contact
                  ?.userAccess({
                    where: {
                      user: {
                        memberships: {
                          tenantId: { _eq: args.teamId },
                          status: { _eq: 'active' },
                          role: { _eq: 'owner' },
                        },
                      },
                    },
                  })
                  .map((x) => {
                    const isDisabled = x.user
                      .memberships({
                        where: {
                          status: { _eq: 'active' },
                          role: { _eq: 'owner' },
                          tenantId: { _eq: args.teamId },
                        },
                        limit: 1,
                      })
                      .map((y) =>
                        (
                          (y.notificationPreferences() ?? []) as string[]
                        ).includes('owner-statements.published.disabled')
                      )[0];

                    return {
                      id: x.user.id,
                      name: formatUserName(x.user),
                      email: x.user.email,
                      role: x.role,
                      isDisabled,
                    };
                  }) ?? []
              );
            })
        );
    },
    {
      skip: !statement?.periodId || !opened,
      variables: {
        periodId: statement?.periodId,
        opened,
        teamId,
      },
    }
  );
};

const StatusConfirmModal = ({
  closeModal: cl,
  opened,
  statement,
}: {
  opened: boolean;
  closeModal: () => void;
  statement: StatementProps;
}) => {
  const { colors, primaryColor } = useMantineTheme();
  const primary = colors[primaryColor][6];
  const { updateStatus } = useLegderStatementStatusMutation();

  const queryData = useLedgerStatementUserAccessQuery({
    opened,
    statement,
  });

  const disableInputs =
    queryData.isLoading === false &&
    queryData.data &&
    (queryData.data.length === 0 || queryData.data.every((x) => x.isDisabled));

  const methods = useForm<FormInputs>({
    values: {
      notification: disableInputs ? 'disabled' : '2hrs',
      message: '',
    },
  });

  const closeModal = () => {
    cl();
    methods.reset({
      notification: '2hrs',
    });
  };

  const options = useMemo<SelectItem<Notification>[]>(
    () => [
      {
        label: 'Send now  ',
        value: 'now',
        icon: <Icon icon="Send2Icon" size={16} />,
      },
      {
        label: 'in 2 hours',
        value: '2hrs',
        icon: <Icon icon="ClockIcon" size={16} />,
      },
      {
        label: 'Do not send',
        value: 'disabled',
        icon: <Icon icon="CrossCircleIcon" size={16} />,
      },
    ],
    []
  );

  const notification = methods.watch('notification');

  return (
    <Modal
      opened={opened}
      onClose={closeModal}
      centered
      onClick={(e) => e.stopPropagation()}
      size={600}
    >
      <Center mb={rem(32)}>
        <Send2Icon size={24} color={primary} />
      </Center>

      <Title order={3} mb="sm" ta="center">
        Update status to published
      </Title>
      <Text c="neutral" ta="center" size="sm" mb={rem(32)}>
        Once the the statement is published, an email is sent to the owners to
        notify them about their new statement.
      </Text>

      <Stack pos="relative" gap={0} align="stretch" mb="xl">
        <LoadingOverlay visible={queryData.isLoading} />

        <DrawerInfoCard
          labelWidth={150}
          rows={[
            {
              icon: UsersIcon,
              title: 'User Access',
              text: queryData.data?.length ? (
                <Group gap={5}>
                  {queryData.data.map((x) => (
                    <Badge
                      key={x.id}
                      sx={{
                        opacity: x.isDisabled ? 0.5 : 1,
                      }}
                      leftIcon={
                        <Icon
                          size={14}
                          icon={x.role === 'viewer' ? 'EyeIcon' : 'Edit3Icon'}
                        />
                      }
                      tooltip={
                        <>
                          {x.email}
                          {x.isDisabled ? (
                            <>
                              <br />
                              (Owner has disabled statement notifications)
                            </>
                          ) : null}
                        </>
                      }
                    >
                      {x.name}
                    </Badge>
                  ))}
                </Group>
              ) : (
                '-'
              ),
            },
          ]}
        />

        <Box
          mb="xs"
          mt="xs"
          sx={{
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Controller
            control={methods.control}
            name="notification"
            disabled={disableInputs}
            defaultValue="2hrs"
            render={({ field, fieldState }) => (
              <InputWrapper label="Send email to owners">
                <InputSelect
                  type="single"
                  value={options.find((o) => o.value === field.value) || null}
                  setValue={(value) =>
                    value?.value && field.onChange(value.value)
                  }
                  data={{
                    options,
                  }}
                  inputProps={{
                    error: fieldState.error?.message,
                    disabled: field.disabled,
                  }}
                  dropdownProps={{
                    width: 200,
                    hideSearch: true,
                  }}
                />
              </InputWrapper>
            )}
          />
          <Controller
            control={methods.control}
            name="message"
            defaultValue=""
            disabled={notification === 'disabled' || disableInputs}
            render={({ field }) => (
              <InputWrapper
                label={
                  <Text component="p" fw={500} mb={5} mt="lg">
                    Message to the owners{' '}
                    <Text span c="gray">
                      (optional)
                    </Text>
                  </Text>
                }
                w="100%"
              >
                <InputTextarea
                  {...field}
                  placeholder="Add a custom message in the email..."
                />
              </InputWrapper>
            )}
          />
        </Box>
      </Stack>

      <Group>
        <Button
          sx={{
            flex: 1,
          }}
          onClick={closeModal}
          disabled={methods.formState.isSubmitting}
        >
          Close
        </Button>
        <Button
          variant="primary"
          loading={methods.formState.isSubmitting}
          onClick={methods.handleSubmit(async (data) => {
            if (!statement) return;

            if (!statement.periodId) {
              showWarnNotification({
                title: 'Missing ownership',
                message: 'Please reach out to support if the issue persists.',
              });
              return;
            }

            return await updateStatus({
              ...statement,
              status: 'published',
              periodId: statement.periodId,
              emailDelivery:
                data.notification === 'disabled' || disableInputs
                  ? undefined
                  : data,
            }).then(closeModal);
          })}
          disabled={!statement || !opened || queryData.isLoading}
          sx={{
            flex: 2,
          }}
        >
          Change status to published
        </Button>
      </Group>
    </Modal>
  );
};

export const useStatementStatusSelectOptions = ({
  iconSize,
  overrides,
}: {
  iconSize: 'xs' | 'sm';
  overrides?: {
    [key in OwnerStatementStatus]?: {
      label?: string;
      description?: string;
    };
  };
}) =>
  useMemo<SelectItem<Exclude<OwnerStatementStatus, 'preview'>>[]>(() => {
    const options: SelectItem<Exclude<OwnerStatementStatus, 'preview'>>[] = [
      {
        label: overrides?.draft?.label ?? 'Draft',
        description: overrides?.draft?.description ?? 'Editable statement',
        value: 'draft',
        icon: <StatementDraftIcon size={iconSize} />,
      },
      {
        label: overrides?.inReview?.label ?? 'In Review',
        description:
          overrides?.inReview?.description ??
          'Statement is locked and can be reviewed by your team members',
        value: 'inReview',
        icon: <StatementInReviewIcon size={iconSize} />,
      },
      {
        label: overrides?.published?.label ?? 'Published',
        description:
          overrides?.published?.description ??
          'Publish to notify the owner and allow them to view on owner portal',
        value: 'published',
        icon: <StatementPublishedIcon size={iconSize} />,
      },
    ];

    return options;
  }, [iconSize, overrides]);
